import React, { useState } from 'react';
import { withStyles } from '@mui/styles';
import {
  Box,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  Typography,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DocumentMSStyle from './DocumentMSStyle';
import { uploadFiles, createDocument } from './dmsApi';

const DocumentUpload = (props) => {
  const { classes } = props;
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    case_number: '',
    client_name: '',
    document_name: '',
    document_type: '',
    description: '',
  });
  const [aiSummary, setAiSummary] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setError(null);

    // Auto-upload when file is selected
    try {
      setUploading(true);
      const response = await uploadFiles([file]);
      if (response.documents && response.documents.length > 0) {
        const uploaded = response.documents[0];
        setUploadedFile(uploaded);
        setAiSummary(uploaded.summary || '');
      }
    } catch (err) {
      setError(err.error || 'Failed to upload file');
      console.error('Upload error:', err);
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setUploadedFile(null);
    setAiSummary('');
  };

  const handleCreateDocument = async () => {
    if (!uploadedFile) {
      setError('Please upload a file first');
      return;
    }

    // Validate all required fields
    if (!formData.case_number) {
      setError('Case Number is required');
      return;
    }

    if (!/^\d+$/.test(formData.case_number)) {
      setError('Case Number must contain only numbers');
      return;
    }

    if (!formData.client_name) {
      setError('Client Name is required');
      return;
    }

    if (!formData.document_name) {
      setError('Document name is required');
      return;
    }

    if (!formData.document_type) {
      setError('Document Type is required');
      return;
    }

    if (!formData.description) {
      setError('Description is required');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      
      const documentData = {
        ...formData,
        user_id: userId,
        documents: [{
          file_url: uploadedFile.file_url,
          summary: aiSummary || uploadedFile.summary || '',
        }],
      };

      await createDocument(documentData);
      setSuccess('Document created successfully!');
      
      // Reset form
      setFormData({
        case_number: '',
        client_name: '',
        document_name: '',
        document_type: '',
        description: '',
      });
      setSelectedFile(null);
      setUploadedFile(null);
      setAiSummary('');
      setDialogOpen(false);
      
      // Refresh document list if callback provided
      if (props.onSuccess) {
        props.onSuccess();
      }
    } catch (err) {
      setError(err.detail || err.error || 'Failed to create document');
      console.error('Create document error:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
    setSuccess(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Box>
      <Button
        className={classes.button}
        onClick={handleOpenDialog}
        startIcon={
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3.33398 8H12.6673" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 3.33398V12.6673" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        sx={{
          backgroundColor: '#131C4C',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#0F1638',
          },
        }}
      >
        Add Document
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '0',
          },
        }}
      >
        <Box style={{ position: 'relative', padding: '32px' }}>
          {/* Close Button */}
          <IconButton
            onClick={handleCloseDialog}
            style={{
              position: 'absolute',
              right: '16px',
              top: '16px',
              color: '#131C4C',
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Header */}
          <Box style={{ marginBottom: '32px' }}>
            <Typography
              variant="h4"
              style={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#131C4C',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
              }}
            >
              Add New Document
            </Typography>
            <Typography
              variant="body2"
              style={{
                fontSize: '14px',
                color: '#6E759A',
                fontFamily: 'Montserrat',
              }}
            >
              Create a new document template
            </Typography>
          </Box>

          {/* Form Fields */}
          <Grid container spacing={2} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} sm={6}>
              <Box className={classes.formGroup}>
                <Box className={classes.label}>Case Number *</Box>
                <TextField
                  className={classes.input}
                  fullWidth
                  required
                  value={formData.case_number}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      setFormData({ ...formData, case_number: value });
                    }
                  }}
                  placeholder="Enter numbers only (e.g., 2025001)"
                  error={formData.case_number && !/^\d+$/.test(formData.case_number)}
                  helperText={formData.case_number && !/^\d+$/.test(formData.case_number) ? 'Only numbers are allowed' : ''}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.formGroup}>
                <Box className={classes.label}>Client Name *</Box>
                <TextField
                  className={classes.input}
                  fullWidth
                  required
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="e.g., John Doe"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.formGroup}>
                <Box className={classes.label}>Document Name *</Box>
                <TextField
                  className={classes.input}
                  fullWidth
                  required
                  value={formData.document_name}
                  onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                  placeholder="e.g., Visa Application Form"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.formGroup}>
                <Box className={classes.label}>Document Type *</Box>
                <TextField
                  className={classes.input}
                  fullWidth
                  required
                  value={formData.document_type}
                  onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                  placeholder="e.g., visa, passport, permit"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.formGroup}>
                <Box className={classes.label}>Description *</Box>
                <TextField
                  className={classes.input}
                  fullWidth
                  required
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter document description"
                  style={{ height: 'auto' }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Upload Document File Section */}
          <Box className={classes.formGroup} style={{ marginBottom: '24px' }}>
            <Box className={classes.label}>Upload Document File</Box>
            {!uploadedFile ? (
              <Box
                className={classes.fileUploadArea}
                onClick={() => document.getElementById('file-input').click()}
                style={{
                  border: '2px dashed #BCC0D5',
                  borderRadius: '9px',
                  padding: '40px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#F9F9F9',
                }}
              >
                <input
                  id="file-input"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <CloudUploadIcon
                  style={{
                    fontSize: '48px',
                    color: '#BCC0D5',
                    marginBottom: '16px',
                  }}
                />
                <Box
                  style={{
                    fontSize: '16px',
                    color: '#131C4C',
                    fontWeight: 600,
                    fontFamily: 'Montserrat',
                  }}
                >
                  Click to upload files or drag and drop
                </Box>
                <Box
                  style={{
                    fontSize: '14px',
                    color: '#6E759A',
                    marginTop: '8px',
                    fontFamily: 'Montserrat',
                  }}
                >
                  PDF, Images, and other document formats
                </Box>
                {uploading && (
                  <Box style={{ marginTop: '16px' }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            ) : (
              <Paper
                style={{
                  padding: '16px',
                  borderRadius: '9px',
                  border: '1px solid #E0E0E0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Box
                    style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#EBE3CA',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionIcon style={{ color: '#131C4C', fontSize: '28px' }} />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Box
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#131C4C',
                        fontFamily: 'Montserrat',
                      }}
                    >
                      {selectedFile?.name || 'Document'}
                    </Box>
                    <Box
                      style={{
                        fontSize: '12px',
                        color: '#6E759A',
                        marginTop: '4px',
                        fontFamily: 'Montserrat',
                      }}
                    >
                      {selectedFile ? formatFileSize(selectedFile.size) : ''}
                    </Box>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleFileRemove}
                  style={{ color: '#131C4C' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            )}
          </Box>

          {/* AI Document Summary Section */}
          <Box className={classes.formGroup} style={{ marginBottom: '24px' }}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}
            >
              <Box className={classes.label}>AI Document Summary</Box>
              <AutoAwesomeIcon
                style={{
                  fontSize: '18px',
                  color: '#131C4C',
                }}
              />
            </Box>
            <TextField
              className={classes.input}
              fullWidth
              multiline
              rows={4}
              value={aiSummary}
              onChange={(e) => setAiSummary(e.target.value)}
              placeholder="AI-generated summary will appear here..."
              style={{ height: 'auto' }}
            />
            <Box
              style={{
                fontSize: '12px',
                color: '#6E759A',
                marginTop: '8px',
                fontFamily: 'Montserrat',
              }}
            >
              This summary was auto-generated. You can edit it if needed.
            </Box>
          </Box>

          {error && (
            <Box
              className={classes.error}
              style={{ marginBottom: '16px', textAlign: 'center' }}
            >
              {error}
            </Box>
          )}

          {success && (
            <Box
              style={{
                color: '#4CAF50',
                fontSize: '14px',
                marginBottom: '16px',
                textAlign: 'center',
                fontFamily: 'Montserrat',
              }}
            >
              {success}
            </Box>
          )}

          {/* Action Buttons */}
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
            }}
          >
            <Button
              onClick={handleCloseDialog}
              className={classes.buttonSecondary}
              style={{ minWidth: '120px' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDocument}
              className={classes.button}
              disabled={creating || !uploadedFile}
              style={{ minWidth: '120px' }}
            >
              {creating ? (
                <CircularProgress size={20} style={{ color: 'white' }} />
              ) : (
                'Create'
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default withStyles(DocumentMSStyle)(DocumentUpload);

